import { useEffect, useState, useCallback, useMemo, useRef, memo } from 'react';
import {
    Dialog,
    Box,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    Button,
    Autocomplete,
    CircularProgress,
    useMediaQuery,
    useTheme,
    createFilterOptions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { useCreatePlan } from '../../hooks/usePlans';
import { useSnackbar } from 'notistack';
import { validatePlanName, validateBrand, hasErrors } from './validation';
import paymentportalservice from '../../services/paymentportalservice';

const fieldSx = {
    mb: 3,
    '& .MuiOutlinedInput-root': {
        background: '#F9FAFB',
        borderRadius: '10px',
        fontSize: 14.5,
        '& fieldset': { borderColor: '#E5E7EB' },
        '&:hover fieldset': { borderColor: '#D1D5DB' },
        '&.Mui-focused fieldset': { borderColor: '#FF5200', borderWidth: '1.5px' },
        '&.Mui-error fieldset': { borderColor: '#EF4444' },
    },
    '& input::placeholder': { color: '#9CA3AF', opacity: 1 },
};

const filterBrands = createFilterOptions();

const sameName = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();

const fetchBrandOptions = async () => {
    const raw = await paymentportalservice.FetchBrandsData();
    const list = Array.isArray(raw) ? raw : (raw?.data || []);
    return list
        .map((b) => ({
            _id: String(b._id || b.id || b.brandId || ''),
            name: b.name || b.brandName || b.title || '',
        }))
        .filter((b) => b._id && b.name);
};

const fieldLabelSx = {
    mb: 1,
    color: '#374151',
    fontWeight: 600,
    fontSize: 13.5,
};

/**
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {(data: any) => void} onCreated
 * @param {string[]} existingNames - names of plans already visible to this user,
 *   used for a client-side duplicate check before hitting the API.
 */
const CreatePlanModal = ({ open, onClose, onCreated, existingNames = [] }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // State
    const [planName, setPlanName] = useState('');
    const [brand, setBrand] = useState(null);
    const [brandOptions, setBrandOptions] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [creatingBrand, setCreatingBrand] = useState(false);
    // Guards against a double click firing two POSTs before the disabled state renders.
    const creatingBrandRef = useRef(false);
    const [brandInput, setBrandInput] = useState('');
    // Result of the DB lookup for the typed name: { name, status: 'checking' | 'available' | 'exists' }
    const [brandCheck, setBrandCheck] = useState(null);
    const [touched, setTouched] = useState({ planName: false, brand: false });
    const [serverErrors, setServerErrors] = useState({});

    const { mutate, isPending } = useCreatePlan();
    const { enqueueSnackbar } = useSnackbar();

    // Memoized validation results
    const clientErrors = useMemo(() => ({
        planName: validatePlanName(planName, existingNames),
        brand: validateBrand(brand),
    }), [planName, existingNames, brand]);

    const isValid = useMemo(() => !hasErrors(clientErrors), [clientErrors]);

    const fieldError = useCallback((field) =>
        touched[field] ? serverErrors[field] || clientErrors[field] : null,
    [touched, serverErrors, clientErrors]);

    // Reset form
    const resetForm = useCallback(() => {
        setPlanName('');
        setBrand(null);
        setTouched({ planName: false, brand: false });
        setServerErrors({});
        setBrandOptions([]);
        setBrandInput('');
        setBrandCheck(null);
    }, []);

    // Close handler
    const handleClose = useCallback(() => {
        resetForm();
        onClose();
    }, [resetForm, onClose]);

    // Create handler
    const handleCreate = useCallback(() => {
        setTouched({ planName: true, brand: true });
        if (!isValid) return;

        mutate(
            {
                name: planName.trim(),
                brandId: brand?._id,
                brandName: brand?.name,
            },
            {
                onSuccess: (data) => {
                    enqueueSnackbar('Plan created', { variant: 'success' });
                    onCreated?.(data);
                    handleClose();
                },
                onError: (error) => {
                    const apiFieldErrors = error?.response?.data?.errors;
                    if (apiFieldErrors && typeof apiFieldErrors === 'object') {
                        setServerErrors(apiFieldErrors);
                    }
                    const message = error?.response?.data?.message ||
                                   "Couldn't create the plan. Try again.";
                    enqueueSnackbar(message, { variant: 'error' });
                },
            }
        );
    }, [isValid, mutate, planName, brand, enqueueSnackbar, onCreated, handleClose]);

    // Fetch brands on open. isMounted guards against a stale response landing after
    // the modal is closed/reopened mid-flight — the cleanup must come from this
    // effect itself (not a callback the effect merely invokes) or React never sees it.
    useEffect(() => {
        if (!open) return;

        let isMounted = true;
        setLoadingBrands(true);

        fetchBrandOptions()
            .then((normalized) => {
                if (isMounted) setBrandOptions(normalized);
            })
            .catch((error) => {
                console.error('Error fetching brands:', error);
                if (isMounted) enqueueSnackbar('Failed to fetch brands', { variant: 'error' });
            })
            .finally(() => {
                if (isMounted) setLoadingBrands(false);
            });

        return () => { isMounted = false; };
    }, [open, enqueueSnackbar]);

    // Memoized handlers for form fields
    const handlePlanNameChange = useCallback((e) => {
        const value = e.target.value;
        setPlanName(value);
        if (serverErrors.planName) {
            setServerErrors(prev => ({ ...prev, planName: null }));
        }
    }, [serverErrors.planName]);

    const handlePlanNameBlur = useCallback(() => {
        setTouched(prev => ({ ...prev, planName: true }));
    }, []);

    // POST /brand only answers "success" (no body), so re-fetch the list and
    // pick the new brand out of it by name. A duplicate (someone else added it
    // meanwhile) is fine too — the re-fetch will contain it either way.
    const createBrand = useCallback(async (rawName) => {
        const name = rawName.trim();
        if (!name || creatingBrandRef.current) return;
        creatingBrandRef.current = true;
        setCreatingBrand(true);
        try {
            // Re-check the DB right before saving: if the brand exists by now,
            // select it instead of creating a duplicate.
            const existing = (await paymentportalservice.FindBrandByName(name))
                .map((b) => ({ _id: String(b._id || ''), name: b.name || '' }))
                .find((b) => b._id && b.name);
            if (existing) {
                setBrandOptions((prev) => (
                    prev.some((p) => p._id === existing._id) ? prev : [...prev, existing]
                ));
                setBrand(existing);
                enqueueSnackbar(`Brand "${existing.name}" already exists — selected it`, { variant: 'info' });
                return;
            }
            try {
                await paymentportalservice.PostBrands({ name });
            } catch (error) {
                if (!String(error?.response?.data || '').includes('Duplicate')) throw error;
            }
            const refreshed = await fetchBrandOptions();
            setBrandOptions(refreshed);
            const created = refreshed.find((b) => sameName(b.name, name));
            if (!created) throw new Error('Brand not found after creation');
            setBrand(created);
            enqueueSnackbar(`Brand "${created.name}" added`, { variant: 'success' });
        } catch (error) {
            console.error('Error creating brand:', error);
            enqueueSnackbar("Couldn't add the brand. Try again.", { variant: 'error' });
        } finally {
            creatingBrandRef.current = false;
            setCreatingBrand(false);
        }
    }, [enqueueSnackbar]);

    const handleBrandChange = useCallback((event, value) => {
        if (serverErrors.brand) {
            setServerErrors(prev => ({ ...prev, brand: null }));
        }
        if (value?.isNew) {
            // Only an explicit click on the "Add brand" row creates a brand —
            // Enter (or any other key) on a highlighted Add row is ignored.
            if (event?.type === 'click') createBrand(value.inputValue);
            return;
        }
        setBrand(value);
    }, [serverErrors.brand, createBrand]);

    const handleBrandInputChange = useCallback((_, value) => {
        setBrandInput(value);
    }, []);

    // Once the user stops typing, ask the DB whether a brand with exactly this
    // name (case-insensitive) exists. The "Add" row only shows after the DB
    // confirms it doesn't; if it does exist, it's merged into the options so
    // the user can simply pick it.
    useEffect(() => {
        const name = brandInput.trim();
        if (!name || brandOptions.some((o) => sameName(o.name, name))) {
            setBrandCheck(null);
            return;
        }

        let isCurrent = true;
        setBrandCheck({ name, status: 'checking' });
        const timer = setTimeout(() => {
            paymentportalservice.FindBrandByName(name)
                .then((matches) => {
                    if (!isCurrent) return;
                    const found = matches
                        .map((b) => ({ _id: String(b._id || ''), name: b.name || '' }))
                        .filter((b) => b._id && b.name);
                    if (found.length) {
                        setBrandOptions((prev) => [
                            ...prev,
                            ...found.filter((f) => !prev.some((p) => p._id === f._id)),
                        ]);
                        setBrandCheck({ name, status: 'exists' });
                    } else {
                        setBrandCheck({ name, status: 'available' });
                    }
                })
                .catch((error) => {
                    console.error('Error checking brand:', error);
                    if (isCurrent) setBrandCheck(null);
                });
        }, 500);

        return () => {
            isCurrent = false;
            clearTimeout(timer);
        };
    }, [brandInput, brandOptions]);

    // Existing brands match as usual. Below them: a "Checking…" row while the
    // DB lookup runs, then an "Add" row only if the DB has no such brand.
    const handleFilterBrands = useCallback((options, params) => {
        const filtered = filterBrands(options, params);
        const input = params.inputValue.trim();
        if (!input || !brandCheck || !sameName(brandCheck.name, input)) return filtered;
        if (brandCheck.status === 'checking') {
            filtered.push({ isChecking: true, name: input });
        } else if (brandCheck.status === 'available') {
            // name is the raw input (not `Add "..."`): MUI writes the option's
            // label into the text field on Enter, and the typed name must stay put.
            // renderBrandOption draws the "Add brand" wording in the list.
            filtered.push({ isNew: true, inputValue: input, name: input });
        }
        return filtered;
    }, [brandCheck]);

    const renderBrandOption = useCallback((props, option) => {
        const { key, ...rest } = props;
        if (option.isChecking) {
            return (
                <li key={key} {...rest} style={{ color: '#9CA3AF', fontSize: 13 }}>
                    <CircularProgress size={14} sx={{ color: '#9CA3AF', mr: 1 }} />
                    Checking if "{option.name}" exists…
                </li>
            );
        }
        if (!option.isNew) return <li key={key} {...rest}>{option.name}</li>;
        return (
            <li key={key} {...rest} style={{ color: '#FF5200', fontWeight: 600 }}>
                <AddIcon sx={{ fontSize: 18, mr: 1 }} />
                Add brand "{option.inputValue}"
            </li>
        );
    }, []);

    const handleBrandBlur = useCallback(() => {
        setTouched(prev => ({ ...prev, brand: true }));
    }, []);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            transitionDuration={{ enter: 260, exit: 180 }}
            PaperProps={{
                elevation: 0,
                sx: {
                    borderRadius: isMobile ? 0 : '20px',
                    overflow: 'hidden',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
                    m: isMobile ? 0 : 2,
                    maxHeight: isMobile ? '100vh' : '90vh',
                },
            }}
        >
            {/* Orange top stripe */}
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #FF5200 0%, #FF8C00 100%)', flexShrink: 0 }} />

            {/* Header */}
            <Box
                sx={{
                    px: { xs: 2.5, sm: 3 },
                    pt: 2.5,
                    pb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #F3F4F6',
                    background: '#fff',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 38, height: 38, borderRadius: '10px',
                            background: 'linear-gradient(135deg, rgba(255,82,0,0.1), rgba(255,140,0,0.06))',
                            border: '1px solid rgba(255,82,0,0.18)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}
                    >
                        <AssignmentOutlinedIcon sx={{ color: '#FF5200', fontSize: 18 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>
                            Create Plan
                        </Typography>
                        <Typography sx={{ color: '#9CA3AF', fontSize: 12, mt: 0.25 }}>
                            Set up a new influencer campaign plan
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    size="small"
                    onClick={handleClose}
                    sx={{
                        color: '#9CA3AF',
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        flexShrink: 0,
                        '&:hover': { background: '#F3F4F6', color: '#374151' },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{ px: { xs: 2.5, sm: 3 }, pt: 2.5, pb: 1, background: '#fff' }}>
                {/* Plan Name */}
                <Typography sx={fieldLabelSx}>Plan Name *</Typography>
                <TextField
                    fullWidth
                    placeholder="Enter plan name"
                    value={planName}
                    onChange={handlePlanNameChange}
                    onBlur={handlePlanNameBlur}
                    error={Boolean(fieldError('planName'))}
                    helperText={fieldError('planName') || `${planName.trim().length}/80`}
                    inputProps={{ maxLength: 80 }}
                    sx={fieldSx}
                />

                {/* Brand */}
                <Typography sx={fieldLabelSx}>Brand *</Typography>
                <Autocomplete
                    options={brandOptions}
                    getOptionLabel={(option) => option?.name ?? ''}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    filterOptions={handleFilterBrands}
                    renderOption={renderBrandOption}
                    getOptionDisabled={(option) => Boolean(option.isChecking)}
                    inputValue={brandInput}
                    onInputChange={handleBrandInputChange}
                    selectOnFocus
                    handleHomeEndKeys
                    value={brand}
                    onChange={handleBrandChange}
                    onBlur={handleBrandBlur}
                    loading={loadingBrands || creatingBrand}
                    disabled={loadingBrands || creatingBrand}
                    noOptionsText="Type a brand name to add it"
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Search or add a brand"
                            error={Boolean(fieldError('brand'))}
                            helperText={fieldError('brand')}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: 17, color: '#9CA3AF' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <>
                                        {(loadingBrands || creatingBrand) && (
                                            <CircularProgress size={16} sx={{ color: '#FF5200' }} />
                                        )}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    sx={fieldSx}
                />
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    px: { xs: 2.5, sm: 3 }, py: 2,
                    borderTop: '1px solid #F3F4F6',
                    display: 'flex',
                    gap: 1.5,
                    background: '#FAFAFA',
                }}
            >
                <Button
                    fullWidth
                    onClick={handleClose}
                    sx={{
                        color: '#6B7280', background: '#fff', borderRadius: '10px',
                        py: 1.1, fontSize: 14, fontWeight: 500, textTransform: 'none',
                        border: '1px solid #E5E7EB', boxShadow: 'none',
                        '&:hover': { background: '#F9FAFB', borderColor: '#D1D5DB', color: '#374151' },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    disabled={isPending || !isValid}
                    onClick={handleCreate}
                    sx={{
                        background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                        borderRadius: '10px', py: 1.1, fontSize: 14, fontWeight: 700,
                        textTransform: 'none', boxShadow: '0 4px 14px rgba(255,82,0,0.3)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                            opacity: 0.9, boxShadow: '0 6px 20px rgba(255,82,0,0.45)',
                        },
                        '&.Mui-disabled': { background: '#F3F4F6', color: '#9CA3AF', boxShadow: 'none' },
                    }}
                >
                    {isPending ? <CircularProgress size={20} color="inherit" /> : 'Create Plan'}
                </Button>
            </Box>
        </Dialog>
    );
};

// Memoize the entire component
export default memo(CreatePlanModal);
